//
// # Netlist Extractor
//
// Extracts a ComputedNetlist from a model Schematic.
// Wraps the existing extractCircuit function and converts
// its output to the unified netlist format.
//

import { Result } from "../errors";
import { schema } from "GenAlphaSchema";
import { extractCircuit, Extraction } from "../circuit/extractor";
import type { Schematic, Instance, Conns, SchPinSpec } from "../schematic";
import { InstanceParam } from "../schematic/param";
import {
  ComputedNetlist,
  Net,
  NetConnection,
  NetlistInstance,
  NetlistPort,
  NetlistExtractionResult,
  NetlistWarning,
  inferNetUsage,
} from "./types";

// Extract a netlist from a model schematic
// The schematic must have symbols resolved on instances
// When expandArrays is true, expands arrayed instances (m > 1) into individual instances:
// e.g., MP2 with m=5 -> MP2.0, MP2.1, MP2.2, MP2.3, MP2.4
export function extractNetlist(
  schematic: Schematic,
  schematicIdent: schema.Ident,
  expandArrays: boolean = false
): Result<NetlistExtractionResult> {
  // 1. Run existing circuit extraction
  // This populates inst.conns on each instance
  const extractionResult = extractCircuit(schematic);

  // Handle extraction errors
  if (extractionResult.err) {
    const extraction = extractionResult.val as Extraction;
    return Result.Err({
      why: "circuit-extraction-failed",
      errors: extraction.errors.map((e) => e.toString()),
    });
  }

  const extraction = extractionResult.val;
  const warnings: NetlistWarning[] = [];

  // Convert extraction warnings
  for (const w of extraction.warnings) {
    warnings.push({
      kind: "unconnected-instance-port",
      message: w.toString(),
      details: w,
    });
  }

  // 2. Build nets from connectables and instance connections
  const nets = buildNets(schematic, extraction.conns, expandArrays);

  // 3. Build instance list
  const instances = buildInstances(schematic, expandArrays);

  // 4. Build port list from schematic pins
  const ports = buildPorts(schematic, extraction.conns);

  const netlist: ComputedNetlist = {
    sourceSchematic: schematicIdent,
    nets,
    instances,
    ports,
  };

  return Result.Ok({ netlist, warnings });
}

// Get the multiplicity (m) parameter from an instance, defaults to 1
export function getMultiplicity(inst: Instance): number {
  for (const p of inst.params) {
    const param = InstanceParam.toObject(p);
    if (param.name === "m") {
      const m = parseInt(param.value, 10);
      return isNaN(m) || m < 1 ? 1 : m;
    }
  }
  return 1;
}

// Generate expanded instance names for an array instance
// e.g., "MP2" with m=3 -> ["MP2.0", "MP2.1", "MP2.2"]
export function expandInstanceNames(
  baseName: string,
  multiplicity: number
): string[] {
  if (multiplicity <= 1) {
    return [baseName];
  }
  const names: string[] = [];
  for (let i = 0; i < multiplicity; i++) {
    names.push(`${baseName}.${i}`);
  }
  return names;
}

// Build nets from the extraction results
// When expandArrays is true, expands arrayed instances based on 'm' parameter
function buildNets(
  schematic: Schematic,
  conns: Conns,
  expandArrays: boolean
): Net[] {
  const nets: Net[] = [];
  const netConnectionsMap = new Map<string, NetConnection[]>();

  // First, collect all connections per signal from instances
  for (const inst of schematic.instances) {
    const expandedNames = expandArrays
      ? expandInstanceNames(inst.name, getMultiplicity(inst))
      : [inst.name];

    for (const conn of inst.conns) {
      const signalName = conn.conn.repr();
      if (!netConnectionsMap.has(signalName)) {
        netConnectionsMap.set(signalName, []);
      }
      // Add a connection for each expanded instance
      for (const expandedName of expandedNames) {
        netConnectionsMap.get(signalName)!.push({
          kind: "inst-port",
          inst: expandedName,
          port: conn.portName,
        });
      }
    }
  }

  // Add module ports to their nets
  for (const [portName, _port] of conns.ports) {
    // The port connects to a signal with the same name
    if (!netConnectionsMap.has(portName)) {
      netConnectionsMap.set(portName, []);
    }
    netConnectionsMap.get(portName)!.push({
      kind: "module-port",
      port: portName,
    });
  }

  // Create Net objects from connectables
  for (const [name, connectable] of conns.connectables) {
    const connections = netConnectionsMap.get(name) || [];
    nets.push({
      name,
      width: connectable.width(),
      usage: inferNetUsage(name),
      connections,
    });
  }

  // Also create nets for ports that aren't in connectables
  // (ports that might not be connected to anything internal)
  for (const [portName, port] of conns.ports) {
    if (!conns.connectables.has(portName)) {
      const connections = netConnectionsMap.get(portName) || [];
      nets.push({
        name: portName,
        width: port.name.width(),
        usage: inferNetUsage(portName),
        connections,
      });
    }
  }

  return nets;
}

// Build instance list from schematic
// When expandArrays is true, expands arrayed instances based on 'm' parameter
function buildInstances(
  schematic: Schematic,
  expandArrays: boolean
): NetlistInstance[] {
  const instances: NetlistInstance[] = [];
  for (const inst of schematic.instances) {
    const expandedNames = expandArrays
      ? expandInstanceNames(inst.name, getMultiplicity(inst))
      : [inst.name];
    for (const expandedName of expandedNames) {
      instances.push({
        name: expandedName,
        target: inst.target,
      });
    }
  }
  return instances;
}

// Build port list from schematic pins
function buildPorts(schematic: Schematic, conns: Conns): NetlistPort[] {
  return schematic.pins.map((pin: SchPinSpec) => {
    const schPort = conns.ports.get(pin.portName);
    return {
      name: pin.portName,
      direction: pin.direction,
      width: schPort ? schPort.name.width() : 1,
      connectedNet: pin.portName, // Port connects to net of same name
    };
  });
}

// Export types
export type { NetlistExtractionResult };
